// covered-by: frontends/carrie/src/services/__tests__/knowledge.test.ts
/**
 * Fetchers for the refreshed Knowledge Database (/knowledge/*): sources
 * (presigned-POST upload + register), rules (read + admin transitions),
 * the append-only activity log, and the relocated read-only memories view.
 */
import {
  createKnowledgeSourcePresignedUrlResponseSchema,
  getKnowledgeActivityResponseSchema,
  knowledgeGraphStatusSchema,
  getKnowledgeRulesResponseSchema,
  getKnowledgeSourceDownloadResponseSchema,
  getKnowledgeSourcesResponseSchema,
  knowledgeRuleResultSchema,
  knowledgeSourceResultSchema,
  type CreateKnowledgeSourcePresignedUrlBody,
  type UpdateKnowledgeScopeBody,
} from "@mg/schemas/src/christo/catalyst";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";
import { parseResponse } from "../utils/parseResponse";
import { getEnterpriseMemoriesResponseSchema } from "../utils/parsers/knowledge-db";

const BASE = `${API_URL}/private/catalyst/knowledge`;

function toQueryString(params: Record<string, unknown>): string {
  const entries: [string, string][] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    entries.push([key, String(value)]);
  }
  const qs = new URLSearchParams(entries).toString();
  return qs ? `?${qs}` : "";
}

/////
// Sources
/////

export async function getKnowledgeSourcePresignedUrl(
  payload: CreateKnowledgeSourcePresignedUrlBody,
) {
  const json = await smartFetch(`${BASE}/sources/presigned-url`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseResponse(createKnowledgeSourcePresignedUrlResponseSchema, json);
}

/** Direct S3 POST against the presigned policy. The file key must be
 * appended last — AWS stops reading form fields after it. */
export async function uploadKnowledgeSourceToS3(
  presignedUrl: string,
  fields: Record<string, string>,
  file: File,
): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  formData.append("file", file, file.name);

  const response = await fetch(presignedUrl, {
    method: "POST",
    body: formData,
  });
  if (!response.ok) {
    let message = `S3 upload failed with status ${response.status}`;
    try {
      const text = await response.text();
      const match = text.match(/<Message>(.+?)<\/Message>/)?.[1];
      if (match) message = `S3 Error: ${match}`;
    } catch {
      // ignore parse failures
    }
    throw new Error(message);
  }
}

export type RegisterKnowledgeSourcePayload = {
  sourceId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  scope?: UpdateKnowledgeScopeBody;
};

/** Register the uploaded file and start ingest. No s3Key in the body — the
 * server reconstructs it from (enterpriseId, sourceId, fileName). */
export async function registerKnowledgeSource(
  payload: RegisterKnowledgeSourcePayload,
) {
  const json = await smartFetch(`${BASE}/sources`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return parseResponse(knowledgeSourceResultSchema, json);
}

export type GetKnowledgeSourcesPayload = {
  status?: string;
  pageSize?: number;
  searchAfter?: string;
};

export async function getKnowledgeSources(
  payload: GetKnowledgeSourcesPayload = {},
) {
  const json = await smartFetch(`${BASE}/sources${toQueryString(payload)}`, {
    method: "GET",
  });
  return parseResponse(getKnowledgeSourcesResponseSchema, json);
}

export async function getKnowledgeSourceDownload(sourceId: string) {
  const json = await smartFetch(
    `${BASE}/sources/${encodeURIComponent(sourceId)}/download`,
    { method: "GET" },
  );
  return parseResponse(getKnowledgeSourceDownloadResponseSchema, json);
}

export async function updateKnowledgeSourceScope(payload: {
  sourceId: string;
  scope: UpdateKnowledgeScopeBody;
}) {
  const json = await smartFetch(
    `${BASE}/sources/${encodeURIComponent(payload.sourceId)}/scope`,
    { method: "PATCH", body: JSON.stringify(payload.scope) },
  );
  return parseResponse(knowledgeSourceResultSchema, json);
}

/** Human usage note. `null` (or a blank string) clears it server-side. */
export async function updateKnowledgeSourceNote(payload: {
  sourceId: string;
  note: string | null;
}) {
  const json = await smartFetch(
    `${BASE}/sources/${encodeURIComponent(payload.sourceId)}/note`,
    { method: "PATCH", body: JSON.stringify({ note: payload.note }) },
  );
  return parseResponse(knowledgeSourceResultSchema, json);
}

export async function retryKnowledgeExtraction(sourceId: string) {
  const json = await smartFetch(
    `${BASE}/sources/${encodeURIComponent(sourceId)}/extract`,
    { method: "POST" },
  );
  return parseResponse(knowledgeSourceResultSchema, json);
}

export async function deleteKnowledgeSource(sourceId: string) {
  await smartFetch(`${BASE}/sources/${encodeURIComponent(sourceId)}`, {
    method: "DELETE",
  });
  return undefined;
}

/////
// Rules
/////

export type GetKnowledgeRulesPayload = {
  status?: string;
  category?: string;
  query?: string;
  sourceId?: string;
  pageSize?: number;
  searchAfter?: string;
};

export async function getKnowledgeRules(
  payload: GetKnowledgeRulesPayload = {},
) {
  const json = await smartFetch(`${BASE}/rules${toQueryString(payload)}`, {
    method: "GET",
  });
  return parseResponse(getKnowledgeRulesResponseSchema, json);
}

/** Fetch a single rule by id — used to resolve a deep-linked citation whose
 * rule isn't on the loaded (paginated) Rules list page. */
export async function getKnowledgeRule(ruleId: string) {
  const json = await smartFetch(`${BASE}/rules/${encodeURIComponent(ruleId)}`, {
    method: "GET",
  });
  return parseResponse(knowledgeRuleResultSchema, json);
}

export async function promoteKnowledgeRule(ruleId: string) {
  const json = await smartFetch(
    `${BASE}/rules/${encodeURIComponent(ruleId)}/promote`,
    { method: "POST" },
  );
  return parseResponse(knowledgeRuleResultSchema, json);
}

export async function rejectKnowledgeRule(ruleId: string) {
  const json = await smartFetch(
    `${BASE}/rules/${encodeURIComponent(ruleId)}/reject`,
    { method: "POST" },
  );
  return parseResponse(knowledgeRuleResultSchema, json);
}

export async function archiveKnowledgeRule(ruleId: string) {
  const json = await smartFetch(
    `${BASE}/rules/${encodeURIComponent(ruleId)}/archive`,
    { method: "POST" },
  );
  return parseResponse(knowledgeRuleResultSchema, json);
}

export async function updateKnowledgeRuleScope(payload: {
  ruleId: string;
  scope: UpdateKnowledgeScopeBody;
}) {
  const json = await smartFetch(
    `${BASE}/rules/${encodeURIComponent(payload.ruleId)}/scope`,
    { method: "PATCH", body: JSON.stringify(payload.scope) },
  );
  return parseResponse(knowledgeRuleResultSchema, json);
}

/////
// Activity
/////

export type GetKnowledgeActivityPayload = {
  targetType?: string;
  targetId?: string;
  pageSize?: number;
  searchAfter?: string;
};

export async function getKnowledgeActivity(
  payload: GetKnowledgeActivityPayload = {},
) {
  const json = await smartFetch(`${BASE}/activity${toQueryString(payload)}`, {
    method: "GET",
  });
  return parseResponse(getKnowledgeActivityResponseSchema, json);
}

/////
// Memories (relocated from /knowledgeDataModel/enterpriseMemories)
/////

export type GetEnterpriseMemoriesPayload = {
  pageSize?: number;
  type?: string;
  query?: string;
  searchAfter?: string; // opaque cursor: `${rawUpdatedAt}|${id}`
};

export async function getEnterpriseMemories(
  payload: GetEnterpriseMemoriesPayload = {},
) {
  const json = await smartFetch(`${BASE}/memories${toQueryString(payload)}`, {
    method: "GET",
  });
  return parseResponse(getEnterpriseMemoriesResponseSchema, json);
}

/** When warhol last built this enterprise's knowledge graph, and whether it is building now. */
export async function getKnowledgeGraphStatus() {
  const json = await smartFetch(`${BASE}/graph`, { method: "GET" });
  return parseResponse(knowledgeGraphStatusSchema, json);
}

/** "Build knowledge graph": signals warhol's refresh workflow for this enterprise. */
export async function requestKnowledgeGraphBuild() {
  const json = await smartFetch(`${BASE}/graph/build`, { method: "POST" });
  return parseResponse(knowledgeGraphStatusSchema, json);
}
